import Text from 'components/commons/text';
import * as React from 'react';
import { CSS, VariantProps } from 'styles';
import typography from 'styles/typography';

import {
  StyledTab,
  StyledTabPanel,
  StyledTabsContainer,
  StyledTabsList,
  TabBadge,
} from './style';

type TabProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
  badges?: string;
};
type TabVariants = VariantProps<typeof StyledTab>;
export type ITabProps = { css?: CSS } & TabProps & TabVariants;

export const Tab = React.forwardRef<HTMLDivElement, ITabProps>((props, ref) => {
  const { children, badges, ...rest } = props;
  return (
    <StyledTab {...rest} ref={ref}>
      <Text
        css={{
          width: '100%',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          ...typography.buttonSmall,
          '@md': typography.buttonNormal,
        }}
        align="center"
      >
        {children}
      </Text>
      {!!badges && <TabBadge>{badges}</TabBadge>}
    </StyledTab>
  );
});

Tab.displayName = 'Tab';

export type TabPanelProps = {
  children: React.ReactNode;
  css?: CSS;
  className?: string;
};
type TabPanelVariants = VariantProps<typeof StyledTabPanel>;
export type ITabPanelProps = TabPanelProps & TabPanelVariants;

const TabPanel = React.forwardRef<HTMLDivElement, ITabPanelProps>(
  (props, ref) => <StyledTabPanel {...props} ref={ref} />,
);

TabPanel.displayName = 'TabPanel';

type TabsProps = {
  children: React.ReactNode;
  defaultActiveKey?: string;
  onChangeTab?: (activeKey: string) => void;
  tabMinWidth?: number;
  tabPanelCss?: CSS;
  tabPanelClassName?: string;
  tabListCss?: CSS;
  tabListClassName?: string;
  tabListItemCss?: CSS;
  tabListItemClassName?: string;
};
type CssProps = { css?: CSS };
type TabsContainerVariants = VariantProps<typeof StyledTabsContainer>;

export type ITabsProps = TabsProps & TabsContainerVariants & CssProps;

export const Tabs = React.forwardRef<HTMLDivElement, ITabsProps>(
  (props, ref) => {
    const {
      children,
      defaultActiveKey,
      onChangeTab,
      tabMinWidth,
      tabPanelClassName,
      tabPanelCss,
      tabListClassName,
      tabListCss,
      tabListItemClassName,
      tabListItemCss,
      ...rest
    } = props;
    const list: any = children || [];
    const [activeKey, setActiveKey] = React.useState(
      defaultActiveKey || (list.length > 0 && list[0].key) || '',
    );

    return (
      <StyledTabsContainer {...rest}>
        <StyledTabsList
          ref={ref}
          css={{ overflowX: 'auto', ...tabListCss }}
          className={tabListClassName}
        >
          {list.map((item, idx) => (
            <Tab
              key={item.key}
              onClick={() => {
                setActiveKey(item.key);
                onChangeTab && onChangeTab(item.key);
              }}
              isSelected={activeKey === item.key}
              // isDisabled={false}
              badges={item.props.badges}
              css={{ ...tabListItemCss, minWidth: tabMinWidth }}
              className={tabListItemClassName}
              // ref={onAddTabRef}
            >
              {item.props.title}
            </Tab>
          ))}
        </StyledTabsList>
        {activeKey && !!list.find((item) => item.key === activeKey) && (
          <TabPanel
            css={tabPanelCss}
            className={tabPanelClassName}
            key={activeKey || ''}
          >
            {list.find((item) => item.key === activeKey).props.children}
          </TabPanel>
        )}
      </StyledTabsContainer>
    );
  },
);

Tabs.displayName = 'Tabs';
